import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { shallowEqual, useSelector } from "react-redux";

import { EMPTY_FILTER_ID } from "constants/app.constants";
import { isForecastTrumpingSource } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import { selectCheckedGlobalForecasts, useForecastStore } from "stores/forecast";
import {
  applyNormalizationDrop,
  buildForecastTrumpingSettings,
  getPropertiesWithForecast
} from "utils/data-table";
import { toLocalYYYYMMDD } from "utils/dateFormatters";

import {
  GroupModeRequestDto,
  NormalizationParams,
  fetchData,
  toGroupModeRequestDto
} from "api/data";
import { getFilterCountsFromFilterId } from "api/filter";

import { useGroupModeState } from "components/filter/production/hooks/useGroupModeController";

import { EntityKind } from "../../../models/entityKind";
import { DataSourceSetting } from "../DataTableActions";
import { changePage, updateWells, useTable } from "../context";

const message =
  "An error occurred connecting to backend services. Please try again at a later time.";

// maximum cell count before pagination
export const MAX_CELL_COUNT = 10000000;

type PageSizing = { pagination: boolean; pageSize: number; maxPageSize: number };

/**
 * Derived during render so the first fetch already uses the capped size.
 *
 * `userPageSize` is the size chosen from the size changer; it is clamped to the
 * cell-limit cap and ignored when pagination is off.
 */
export const getPageSize = (
  propertyCount: number,
  totalResults: number,
  userPageSize: Nullable<number>
): PageSizing => {
  const maxPageSize = propertyCount
    ? Math.floor(MAX_CELL_COUNT / propertyCount)
    : totalResults;
  const pagination = totalResults > maxPageSize;
  return {
    pagination,
    pageSize: pagination
      ? Math.min(userPageSize ?? maxPageSize, maxPageSize)
      : totalResults,
    maxPageSize
  };
};

type FilterPagination = PageSizing & {
  totalResults: number;
  countsReady: boolean;
  isFetching: boolean;
  isError: boolean;
};

/**
 * Fetches the current filter's counts itself, rather than reading the Redux value
 * another consumer dispatched, so the data query can wait for the count that
 * matches the current filter instead of fetching once per count update.
 */
const useFilterPagination = (
  filterId: RootState["filter"]["filterId"],
  propertyCount: number,
  userPageSize: Nullable<number>,
  enabled: boolean
): FilterPagination => {
  const isEmptyFilter = !filterId.id || filterId.id === EMPTY_FILTER_ID;
  const countsQuery = useQuery(
    // lastUpdated matches the data query's key, so a filter refresh waits for fresh counts.
    ["filterCounts", filterId.id, filterId.lastUpdated],
    () => getFilterCountsFromFilterId(filterId.id).then((response) => response.data),
    {
      enabled: enabled && !isEmptyFilter,
      refetchOnWindowFocus: false,
      // Keep the previous filter's counts on screen while the new filter's counts
      // load. Without this, countsReady flips true -> false -> true on every filter
      // change, remounting <Pagination> and re-opening the "Large amount of data" dialog.
      keepPreviousData: true
    }
  );
  // keepPreviousData keeps serving the last filter's counts even while disabled.
  const totalResults = isEmptyFilter
    ? 0
    : countsQuery.data?.Active ?? countsQuery.data?.UniqueId ?? 0;
  return {
    ...getPageSize(propertyCount, totalResults, userPageSize),
    totalResults,
    // EMPTY_FILTER_ID never fetches counts (totalResults=0 is correct for it); any
    // other filterId must wait for ITS OWN counts, not the kept-around previous ones.
    countsReady: isEmptyFilter || (countsQuery.isSuccess && !countsQuery.isPreviousData),
    isFetching: countsQuery.isFetching,
    isError: countsQuery.isError
  };
};

/**
 * Simple `useQuery` abstraction to fetch data from endpoint.
 */
const useDataQuery = (
  selectedProperties: string[] | undefined,
  dataSources: DataSourceSetting,
  widgetKey: string,
  // Paginated (large) loads wait until the user confirms them.
  largeLoadConfirmed: boolean,
  // Off while another view (monthly production) owns the table's data.
  enabled = true
) => {
  // context
  const [
    {
      page,
      sortColumn,
      sortDirection,
      entityKind,
      dataTableView,
      pageSize: userPageSize
    },
    dispatch
  ] = useTable();

  const { isGroupMode, groupBy } = useGroupModeState(entityKind ?? EntityKind.Well);

  // state from store
  const filterId = useSelector((state: RootState) =>
    entityKind == EntityKind.Well ? state.filter.filterId : state.facilityFilter.filterId
  );
  const useNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );

  const {
    pagination,
    pageSize,
    maxPageSize,
    totalResults,
    countsReady,
    isFetching: countsFetching,
    isError: countsError
  } = useFilterPagination(
    filterId,
    selectedProperties?.length ?? 0,
    userPageSize,
    enabled
  );

  // A page picked for the previous filter, or before the table stopped paginating,
  // can point past the end of the new results. Deriving the page too keeps the
  // render where pagination turns off from fetching with the stale index.
  const currentPage = pagination ? page : 1;
  useEffect(() => {
    changePage(dispatch, 1);
  }, [dispatch, filterId.id, filterId.lastUpdated, pagination]);

  // state
  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [droppedNormalizationCount, setDroppedNormalizationCount] = useState(0);
  const [normalizedFieldName, setNormalizedFieldName] = useState("");
  const { defaultSignificantFigures, useStrictRounding } = useSelector(
    (state: RootState) => state.app.significantFiguresSettings
  );
  const specialForecastFolders = useSelector(
    (state: RootState) => state.app.userForecastSettings.specialForecastFolders,
    shallowEqual
  );
  const forecastReserveCategory = useSelector(
    (state: RootState) => state.app.userForecastSettings.reserveCategory
  );
  const checkedGlobalForecasts = useForecastStore(selectCheckedGlobalForecasts);
  const syncWells = useSelector((state: RootState) => state.app.syncWells);

  const queryFn = () => {
    const properties = getPropertiesWithForecast(selectedProperties ?? [], dataSources);

    const params = {
      pdenSource: dataSources.pdenSource,
      pageSize: pageSize,
      pageIndex: currentPage - 1,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
      entityKind
    };
    const performanceDateRange =
      dataSources.startDate && dataSources.endDate
        ? {
            startDate: toLocalYYYYMMDD(dataSources.startDate),
            endDate: toLocalYYYYMMDD(dataSources.endDate)
          }
        : undefined;
    const normalization: NormalizationParams = {
      useNormalizeBy,
      globalNormalizeBy
    };
    const isForecastTrumping = isForecastTrumpingSource(dataSources.forecastSource);
    const trumpingSettings = buildForecastTrumpingSettings(
      checkedGlobalForecasts,
      specialForecastFolders,
      syncWells?.isSync ? syncWells.schema : undefined,
      syncWells?.isSync ? syncWells.forecastRescat : undefined,
      forecastReserveCategory
    );
    // REQ-DT-006: Attach groupMode in two cases:
    // 1. Group view — aggregate rows by bin, keyed by group label.
    // 2. Well view — always send groupMode with wellView:true so the backend
    //    returns individual wells within the group context. For binned
    //    numeric/date fields, the backend also replaces raw property values
    //    with bin labels (e.g. "3000 - 3100" instead of "3071") so the
    //    frozen "Group" column is correct.
    let groupMode: GroupModeRequestDto | undefined;
    if (isGroupMode && groupBy?.mode) {
      if (dataTableView === "group") {
        const dto = toGroupModeRequestDto(groupBy.mode);
        if (dto) {
          groupMode = { ...dto, wellView: false };
        }
      } else {
        // Well view: always send groupMode with wellView:true regardless of
        // whether the group field is binned. Non-binned text fields (e.g.
        // Header.ResourcePlay) still need the groupMode context on the backend.
        const dto = toGroupModeRequestDto(groupBy.mode);
        if (dto) {
          groupMode = { ...dto, wellView: true };
        }
      }
    }

    return fetchData(filterId.id, properties, params, {
      normalization,
      defaultSignificantFigures,
      useStrictRounding,
      forecastTrumpingSettings:
        isForecastTrumping &&
        (trumpingSettings.userForecastFolderId || specialForecastFolders.length > 0)
          ? trumpingSettings
          : undefined,
      groupMode,
      performanceDateRange
    });
  };

  const successHandler = (data) => {
    setErrorMessage(null);

    if (entityKind === EntityKind.Well && useNormalizeBy && globalNormalizeBy?.field) {
      const fieldName = globalNormalizeBy.display || globalNormalizeBy.field;
      const { transformedData, droppedCount } = applyNormalizationDrop(
        data,
        selectedProperties ?? [],
        globalNormalizeBy.field
      );
      setDroppedNormalizationCount(droppedCount);
      setNormalizedFieldName(fieldName);
      updateWells(dispatch, transformedData as Record<string, string[]>);
    } else {
      setDroppedNormalizationCount(0);
      setNormalizedFieldName("");
      updateWells(dispatch, data);
    }
  };

  const errorHandler = (error) => {
    setDroppedNormalizationCount(0);
    setNormalizedFieldName("");
    if (error instanceof Error) {
      setErrorMessage(error.message);
      return;
    }
    setErrorMessage(message);
  };

  const queryInfo = useQuery({
    queryKey: [
      widgetKey,
      filterId,
      currentPage,
      pageSize,
      sortColumn,
      sortDirection,
      useNormalizeBy,
      globalNormalizeBy,
      defaultSignificantFigures,
      useStrictRounding,
      // Every checked entry's folder and category, not just [0]: the builder scans all rows of
      // the sent folder for its reserve category, so toggling a second row must refetch.
      checkedGlobalForecasts
        .map((f) => `${f.folderId}:${f.reserveCategory ?? ""}`)
        .join(","),
      specialForecastFolders,
      forecastReserveCategory,
      dataTableView, // REQ-UI-TOG-003 — invalidate on view toggle
      // undefined hashes as null in the key (react-query JSON.stringify), so no `?? null` needed.
      groupBy?.mode?.property, // invalidate on group property change
      groupBy?.mode?.aggregation, // invalidate on aggregation change
      // bin scalar fields listed individually to avoid JSON.stringify key-order instability:
      groupBy?.mode?.bin?.BinSize,
      groupBy?.mode?.bin?.BinType,
      groupBy?.mode?.bin?.LessThan,
      groupBy?.mode?.bin?.GreaterThan,
      groupBy?.mode?.bin?.MinSize,
      groupBy?.mode?.bin?.MaxBins,
      groupBy?.mode?.bin?.UseDynamicBins,
      groupBy?.mode?.bin?.Quantile,
      groupBy?.mode?.bin?.IsLocked,
      selectedProperties ? JSON.stringify(selectedProperties) : null,
      ...Object.values(dataSources)
    ],
    queryFn: queryFn,
    onError: errorHandler,
    onSuccess: successHandler,
    refetchOnWindowFocus: false,
    enabled:
      enabled &&
      selectedProperties?.length > 0 &&
      countsReady &&
      (!pagination || largeLoadConfirmed)
  });

  return {
    isFetching: queryInfo.isFetching || countsFetching,
    isSuccess: queryInfo.isSuccess,
    refetch: queryInfo.refetch,
    errorMessage: errorMessage ?? (countsError ? message : null),
    droppedNormalizationCount,
    normalizedFieldName,
    pagination,
    pageSize,
    maxPageSize,
    totalResults
  };
};

export default useDataQuery;
